import React from 'react';
import { Dialog, DialogContent, DialogTitle, Grid, Stack, Typography, Button, IconButton } from '@mui/material';
import { theme } from '../../../theme';
import { ArrowBackIos, InfoOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ItemReplacementEntry from './ItemReplacementEntry';
import ItemReplacementDetails from './ItemReplacementDetails';
import { useGetItemReplacementsQuery } from '../../../api/physicianDashboardSlice';
import LoadingIndicator from '../../LoadingIndicator';

const isReviewed = (replacement) => ['accepted', 'partially_reviewed', 'rejected'].includes(replacement.replacement_status);

function ItemReplacements() {
    const navigate = useNavigate();
    const { physicianId } = useParams();
    const [isShowingExplanation, setIsShowingExplanation] = React.useState(false);
    const [selectedReplacement, setSelectedReplacement] = React.useState(null);
    const [replacementOrder, setReplacementOrder] = React.useState(null);

    const { data: replacements = [], isLoading: replacementsLoading } = useGetItemReplacementsQuery(physicianId);

    const hospitalCount = new Set(replacements.flatMap((replacement) => (replacement.recommendations ?? []).map((recommendation) => recommendation.card?.hospital.name).filter(Boolean))).size;    
    const sortedReplacements = [...replacements].sort((first, second) => (second.replacement_value ?? 0) - (first.replacement_value ?? 0));
    const replacementsToReview = sortedReplacements.filter((replacement) => !isReviewed(replacement));
    const reviewedReplacements = sortedReplacements.filter(isReviewed);
    const displayedReplacements = [...replacementsToReview, ...reviewedReplacements];

    const replacementKey = (replacement) => replacement.item?.id;

    const openReplacement = (replacement) => {
        setReplacementOrder((currentOrder) => currentOrder ?? displayedReplacements.map(replacementKey));
        setSelectedReplacement(replacement);
    };

    const closeReplacement = () => {
        setSelectedReplacement(null);
        setReplacementOrder(null);
    };

    const openNextReplacement = () => {
        if (!selectedReplacement || !replacementOrder?.length) return;

        const currentIndex = replacementOrder.indexOf(replacementKey(selectedReplacement));
        const nextKey = replacementOrder[(currentIndex + 1) % replacementOrder.length];
        const nextReplacement = replacements.find((replacement) => replacementKey(replacement) === nextKey);

        if (nextReplacement) {
            setSelectedReplacement(nextReplacement);
        }
    };

    return (
        <Grid container sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.itemSwap }}>
            <Grid item md={3} />
            <Grid item xs={12} md={6}>
                <Stack sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.itemSwap }}>
                    <Stack spacing={1.5} p={2} sx={{ backgroundColor: theme.palette.background.white, boxShadow: 5 }}>
                        <Button
                            variant='text'
                            startIcon={<ArrowBackIos />}
                            onClick={() => navigate('/physician/dashboard')}
                            sx={{ alignSelf: 'flex-start', color: 'hold.main', p: 0 }}
                        >
                            All Campaigns
                        </Button>
                        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
                            <Stack spacing={0.5}>
                                <Typography variant='heavy' sx={{ fontSize: '18px' }}>Item Replacements</Typography>
                                <Typography variant='normal' sx={{ fontSize: '14px', color: theme.palette.blue.main }}>
                                    {`${replacements.length} available replacement${replacements.length === 1 ? '' : 's'} across ${hospitalCount} hospital${hospitalCount === 1 ? '' : 's'}`}
                                </Typography>
                            </Stack>
                            <IconButton aria-label='About item replacements' onClick={() => setIsShowingExplanation(true)}>
                                <InfoOutlined sx={{ color: theme.palette.blue.main }} />
                            </IconButton>
                        </Stack>
                    </Stack>
                    <Dialog open={isShowingExplanation} onClose={() => setIsShowingExplanation(false)}>
                        <DialogTitle>
                            What are these replacements?
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant='normal' sx={{ fontSize: '14px' }}>
                                These item replacements propose swapping the item on every card across all facilities in which you operate.
                            </Typography>
                        </DialogContent>
                    </Dialog>
                    { replacementsLoading && <LoadingIndicator variant='replacements' /> }
                    { !replacementsLoading &&
                        <Stack spacing={2} p={2}>
                            {replacementsToReview.map((replacement) => (
                                <ItemReplacementEntry key={replacement.item.id} replacement={replacement} onClick={() => openReplacement(replacement)} />
                            ))}
                            {reviewedReplacements.length > 0 && (
                                <Stack spacing={1} pt={1}>
                                    <Typography variant='heavy' sx={{ fontSize: '16px' }}>Reviewed</Typography>
                                    {reviewedReplacements.map((replacement) => (
                                        <ItemReplacementEntry key={replacement.item.id} replacement={replacement} onClick={() => openReplacement(replacement)} />
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    }
                    <ItemReplacementDetails
                        replacement={selectedReplacement}
                        cardOwnerId={physicianId}
                        onBack={closeReplacement}
                        navigationAction={displayedReplacements.length > 1 ? {
                            label: 'Next Replacement',
                            onClick: openNextReplacement
                        } : null}
                    />
                </Stack>
            </Grid>
            <Grid item md={3} />
        </Grid>
    );
}

export default ItemReplacements;
